import { IDataLayerObject, IDataLayerProduct, IEnhancedEcommerceObject } from '@common/interfaces/data-layer.interface';
import sha256 from 'crypto-js/sha256';

declare global {
    interface Window {
        dataLayer?: IDataLayerObject[];
    }
}

const pushToDataLayer = (obj: IDataLayerObject): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
};

const setUserData = (userId: string, userEmail: string, userPhoneNumber: string): void => {
    const hashedEmail = userEmail ? hashEmail(userEmail) : '';
    pushToDataLayer({
        event: 'customUser',
        userData: {
            userId,
            userEmail,
            hashedEmail,
            userPhoneNumber,
        },
    });
};

const createPageEvent = (path: string): void => {
    const obj: IDataLayerObject = {
        event: 'customPage',
        pageData: {
            path,
        },
    };
    pushToDataLayer(obj);
};

const createCheckoutEnhancedEcommerceEvent = (step: string, product?: IDataLayerProduct[]): void => {
    const nullObj: IEnhancedEcommerceObject = {
        ecommerce: null,
    };

    const obj: IEnhancedEcommerceObject = {
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: { step },
                ...(product && { products: product }),
            },
        },
    };
    pushToDataLayer(nullObj); // Clear the previous ecommerce object.
    pushToDataLayer(obj);
};

const createPurchaseEnhancedEcommerceEvent = (
    revenue: string,
    id: string,
    shipping: string,
    coupon: string,
    product?: IDataLayerProduct[],
): void => {
    const nullObj: IEnhancedEcommerceObject = {
        ecommerce: null,
    };

    const obj: IEnhancedEcommerceObject = {
        event: 'purchase',
        ecommerce: {
            purchase: {
                actionField: {
                    id,
                    revenue,
                    shipping,
                    coupon,
                },
                ...(product && { products: product }),
            },
        },
    };

    pushToDataLayer(nullObj);
    pushToDataLayer(obj);
};

const createGenericEvent = (
    category: string,
    action: string,
    label?: string,
    value?: string,
    nonInteraction?: boolean,
): void => {
    const obj: IDataLayerObject = {
        event: 'customEvent',
        eventData: {
            category,
            action,
            label,
            value,
            nonInteraction,
        },
    };
    pushToDataLayer(obj);
};

const hashEmail = (email: string): string => {
    const hashedEmail = sha256(email).toString();
    return hashedEmail;
};

export const dataLayer = {
    createPageEvent,
    createGenericEvent,
    setUserData,
    createCheckoutEnhancedEcommerceEvent,
    createPurchaseEnhancedEcommerceEvent,
    hashEmail,
};
